// src/markup/pages/keyword-landing.jsx
import React, { Component } from 'react';
import { Redirect, withRouter, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import Header from '../layout/header';
import Footer from '../layout/footer';
import Modal from '../elements/modal';
import { getLocalItem, getSessionItem } from '../../utils/storage';

// Premium components
import {
  PremiumHero,
  StickyMobileCTA,
  SectionWrapper,
  StepCards,
  WhyUsGrid,
  FaqAccordion,
  FinalCTA,
  Breadcrumbs,
  RelatedLinks,
  BlogCard,
  StructuredData,
  BrandTrustBand,
  RelatedTopServices,
} from '../elements/premium';

import brandsData from '../../data/brands_data.json';
import { getStartingPrice } from '../../utils/pricing';

const DEFAULT_HERO = { h1: 'Doorstep Vehicle Service', sub: 'Book in 60 seconds' };

const STEPS = [
  { no: '01', icon: '\uD83D\uDCF1', title: 'Book Online', desc: 'Select your vehicle, choose a service, pick a time slot. Takes under 60 seconds.' },
  { no: '02', icon: '\uD83D\uDE90', title: 'Mechanic Arrives', desc: 'Our certified, uniformed mechanic arrives at your doorstep \u2014 on time, every time.' },
  { no: '03', icon: '\uD83D\uDD27', title: 'Service Done', desc: 'Professional service with genuine parts. Real-time updates and photos shared via app.' },
  { no: '04', icon: '\u2705', title: 'Warranty Included', desc: 'Digital job card, before/after photos, and a 30-day service warranty \u2014 guaranteed.' },
];

const TRUST_ITEMS = [
  { value: '2,00,000+', label: 'Customers Served' },
  { value: '4.8\u2605', label: 'Customer Rating' },
  { value: '32+', label: 'Cities in India' },
  { value: '30-Day', label: 'Service Warranty' },
];

const WHY_ICONS = ['\uD83C\uDFE0', '\uD83C\uDF93', '\uD83D\uDCB0', '\uD83D\uDD12', '\uD83D\uDCF8', '\u26A1', '\uD83D\uDEE1\uFE0F', '\u2705'];

const CATEGORY_BREADCRUMBS = {
  'bike-general': { label: 'Bike Services', href: '/bike-service-near-me' },
  'bike-repair': { label: 'Bike Repair', href: '/bike-repair-near-me' },
  'bike-engine': { label: 'Bike Engine', href: '/bike-engine-repair-near-me' },
  'bike-brake': { label: 'Bike Brake', href: '/bike-brake-repair-near-me' },
  'bike-tyre': { label: 'Bike Tyre', href: '/bike-tyre-replacement-near-me' },
  'bike-battery': { label: 'Bike Battery', href: '/bike-battery-replacement-near-me' },
  'bike-clutch': { label: 'Bike Clutch', href: '/bike-clutch-repair-near-me' },
  'bike-suspension': { label: 'Bike Suspension', href: '/bike-suspension-repair-near-me' },
  'bike-electrical': { label: 'Bike Electrical', href: '/bike-electrical-repair-near-me' },
  'bike-painting': { label: 'Bike Painting', href: '/bike-painting-near-me' },
  'bike-emergency': { label: 'Bike Emergency', href: '/bike-breakdown-service-near-me' },
  'car-general': { label: 'Car Services', href: '/car-service-near-me' },
  'car-repair': { label: 'Car Repair', href: '/car-repair-near-me' },
  'car-ac': { label: 'Car AC Repair', href: '/car-ac-repair-near-me' },
  'car-engine': { label: 'Car Engine', href: '/car-engine-repair-near-me' },
  'car-brake': { label: 'Car Brake', href: '/car-brake-repair-near-me' },
  'car-tyre': { label: 'Car Tyre', href: '/car-tyre-replacement-near-me' },
  'car-battery': { label: 'Car Battery', href: '/car-battery-replacement-near-me' },
  'car-clutch': { label: 'Car Clutch', href: '/car-clutch-repair-near-me' },
  'car-suspension': { label: 'Car Suspension', href: '/car-suspension-repair-near-me' },
  'car-electrical': { label: 'Car Electrical', href: '/car-electrical-repair-near-me' },
  'car-painting': { label: 'Car Painting', href: '/car-painting-near-me' },
  'car-emergency': { label: 'Car Emergency', href: '/car-breakdown-service-near-me' },
  'car-ev': { label: 'EV Services', href: '/electric-car-service' },
};

function getKeywordDataFromWindow() {
  if (typeof window !== 'undefined' && window.__KEYWORD_PAGE_DATA__) {
    return window.__KEYWORD_PAGE_DATA__;
  }
  if (typeof global !== 'undefined' && global.__KEYWORD_PAGE_DATA__) {
    return global.__KEYWORD_PAGE_DATA__;
  }
  return null;
}

function normalizeData(raw) {
  if (!raw) return null;
  if (typeof raw === 'string') {
    return { hero: { h1: raw, sub: '' }, metaTitle: raw, metaDescription: raw };
  }
  if (typeof raw === 'object') {
    if (!raw.hero) raw.hero = { ...DEFAULT_HERO };
    return raw;
  }
  return null;
}

class KeywordLanding extends Component {
  constructor(props) {
    super(props);
    const ssrPayload = getKeywordDataFromWindow();
    if (ssrPayload) {
      this.state = {
        data: normalizeData(ssrPayload.data),
        relationship: ssrPayload.relationship || null,
        categoryMeta: ssrPayload.categoryMeta || null,
        relatedLinks: ssrPayload.relatedLinks || [],
        loading: false,
        notFound: false,
      };
    } else {
      this.state = {
        data: null,
        relationship: null,
        categoryMeta: null,
        relatedLinks: [],
        loading: true,
        notFound: false,
      };
    }
  }

  componentDidMount() {
    const { setOpenModal, isMobileApp, history } = this.props;
    if (
      getLocalItem('phone_provided') === 'yes' ||
      getSessionItem('phone_ignored') === 'yes' ||
      this.props.user.is_logged
    ) {
      setOpenModal(false);
    }
    if (isMobileApp) {
      history.push('/register');
      return;
    }

    if (this.state.data) {
      if (typeof window !== 'undefined') {
        delete window.__KEYWORD_PAGE_DATA__;
      }
      return;
    }

    this.fetchKeywordData();
  }

  fetchKeywordData() {
    const slug = this.props.slug;
    fetch(`/_data/keyword/${encodeURIComponent(slug)}`)
      .then(res => {
        if (!res.ok) throw new Error('Not found');
        return res.json();
      })
      .then(payload => {
        this.setState({
          data: normalizeData(payload.data),
          relationship: payload.relationship || null,
          categoryMeta: payload.categoryMeta || null,
          relatedLinks: payload.relatedLinks || [],
          loading: false,
        });
      })
      .catch(() => {
        this.setState({ notFound: true, loading: false });
      });
  }

  getBreadcrumbs() {
    const { data, categoryMeta } = this.state;
    const items = [{ label: 'Home', href: '/' }];
    if (categoryMeta) {
      const catBread = CATEGORY_BREADCRUMBS[categoryMeta.category];
      if (catBread && catBread.href !== `/${this.props.slug}`) {
        items.push(catBread);
      }
    }
    items.push({ label: data.hero.h1 });
    return items;
  }

  getRelatedBlogs() {
    const { blogsData } = this.props;
    if (!blogsData || !blogsData.length) return [];
    return blogsData.slice(0, 3);
  }

  render() {
    const { data, relatedLinks, loading, notFound, categoryMeta } = this.state;

    if (notFound) return <Redirect to="/error-404" />;

    if (loading || !data) {
      return (
        <div className="rr-home rr-home--lux">
          <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ color: '#999', fontSize: 16 }}>Loading...</p>
          </div>
        </div>
      );
    }

    const {
      isMobileBrowser,
      isMobileApp,
      user,
      setUser,
      setCitiesModal,
      openModal,
      setOpenModal,
    } = this.props;

    const slug = this.props.slug;
    const canonical = `https://www.ridenrepair.com/${slug}`;
    const h1 = data.hero.h1;
    // Price comes from the page JSON (set by the snapshot-driven regenerator).
    // The fallback is vehicle-type-aware (never a blind \u20B9450) so a car page can
    // never show the bike floor if pricingHint is somehow absent.
    const fallbackVt = categoryMeta && categoryMeta.vehicleType === 'car' ? 'car'
      : categoryMeta && categoryMeta.vehicleType === 'bike' ? 'bike' : 'site';
    const pricingHint = data.pricingHint || getStartingPrice(fallbackVt).label;
    const relatedBlogs = this.getRelatedBlogs();

    const schemas = [
      {
        '@type': 'Service',
        name: h1,
        provider: { '@type': 'Organization', name: 'Ride N Repair' },
        description: data.metaDescription,
        areaServed: { '@type': 'Country', name: 'India' },
      },
    ];
    if (data.faqs && data.faqs.length > 0) {
      schemas.push({
        '@type': 'FAQPage',
        mainEntity: data.faqs.map(f => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      });
    }

    const whyUsItems = (data.whyPoints || []).slice(0, 6).map((pt, i) => ({
      icon: WHY_ICONS[i] || '\u2705',
      title: pt.length > 40 ? pt.split('\u2014')[0].split('\u2013')[0].split(',')[0].trim() : pt,
      desc: pt,
    }));

    return (
      <div className="rr-home rr-home--lux">
        <Helmet>
          <title>{data.metaTitle}</title>
          <meta name="description" content={data.metaDescription} />
          <meta name="keywords" content={`${h1}, bike service, car service, doorstep repair, Ride N Repair`} />
          <link rel="canonical" href={canonical} />
          <meta property="og:title" content={data.metaTitle} />
          <meta property="og:description" content={data.metaDescription} />
          <meta property="og:url" content={canonical} />
          <meta property="og:type" content="website" />
        </Helmet>

        <StructuredData data={schemas} />

        <Header
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          user={user}
          setUser={setUser}
        />

        {!isMobileApp && openModal && (
          <Modal
            isMobileBrowser={isMobileBrowser}
            isMobileApp={isMobileApp}
            openModal={openModal}
            setOpenModal={setOpenModal}
            user={user}
            setUser={setUser}
          />
        )}

        <PremiumHero
          badge="Doorstep Service"
          title={h1}
          priceTag={`Starting ${pricingHint}`}
          subtitle={data.hero.sub}
          isMobileBrowser={isMobileBrowser}
          showCarAnimation
          ctas={[
            { label: `Book Now \u2014 ${pricingHint} Onwards`, href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'secondary' },
          ]}
          trustItems={TRUST_ITEMS}
        />

        {/* Trust band removed — PremiumHero trustItems already render inline
            inside the hero. No second band needed below. */}

        <Breadcrumbs items={this.getBreadcrumbs()} />

        <StickyMobileCTA
          title={h1}
          priceHint={`Starting ${pricingHint} \u00B7 30-Day Warranty`}
          btnLabel="Book Now"
          btnHref="/service"
        />

        <SectionWrapper kicker="Overview" heading={h1}>
          <div className="rr-keyword__intro">
            <span dangerouslySetInnerHTML={{ __html: data.introHtml }} />
          </div>
        </SectionWrapper>

        <SectionWrapper variant="gray" kicker="Transparent Pricing" heading={`${h1} \u2014 Starting Price`}>
          <div className="rr-keyword__pricing-preview">
            <div className="rr-keyword__price-card">
              <div className="rr-keyword__price-card-label">Starting From</div>
              <div className="rr-keyword__price-card-amount">{pricingHint}</div>
              <div className="rr-keyword__price-card-note">All-inclusive &middot; No hidden charges</div>
            </div>
            <div className="rr-keyword__price-card">
              <div className="rr-keyword__price-card-label">Warranty</div>
              <div className="rr-keyword__price-card-amount">30 Days</div>
              <div className="rr-keyword__price-card-note">On parts and labour</div>
            </div>
            <div className="rr-keyword__price-card">
              <div className="rr-keyword__price-card-label">Cities</div>
              <div className="rr-keyword__price-card-amount">32+</div>
              <div className="rr-keyword__price-card-note">Pan-India doorstep service</div>
            </div>
          </div>
          <div className="rr-text-center rr-mt-32">
            <Link to="/service" className="rr-cta-primary">Get Exact Price for Your Vehicle</Link>
          </div>
        </SectionWrapper>

        <SectionWrapper variant="dark" kicker="Simple Process" heading="How It Works">
          <StepCards steps={STEPS} variant="dark" />
        </SectionWrapper>

        {whyUsItems.length > 0 && (
          <SectionWrapper variant="gray" kicker="Our Promise" heading="Why 2,00,000+ Customers Choose Ride N Repair">
            <WhyUsGrid items={whyUsItems} />
          </SectionWrapper>
        )}

        {data.faqs && data.faqs.length > 0 && (
          <SectionWrapper narrow kicker="Common Questions" heading={`${h1} \u2014 FAQ`}>
            <FaqAccordion faqs={data.faqs} withSchema={false} />
          </SectionWrapper>
        )}

        {relatedLinks.length > 0 && (
          <SectionWrapper kicker="Related Services" heading="Related Doorstep Services">
            <RelatedLinks links={relatedLinks} />
          </SectionWrapper>
        )}

        {(() => {
          const slugLower = slug.toLowerCase();
          const allBrands = [...(brandsData.bike || []), ...(brandsData.car || [])];
          const seen = new Set();
          const deduped = allBrands.filter(b => { if (seen.has(b.slug)) return false; seen.add(b.slug); return true; });
          const matched = deduped.find(b => slugLower.includes(b.slug));
          const displayBrands = matched ? [matched] : deduped.slice(0, 8);
          const isSpecific = !!matched;
          return (
            <SectionWrapper variant={isSpecific ? 'default' : 'gray'} kicker={isSpecific ? matched.label : 'Popular Brands'} heading={isSpecific ? `${matched.label} \u2014 Doorstep Service` : 'Brands We Service'}>
              <div className="rr-brand-grid">
                {displayBrands.map(b => {
                  let logoSrc = null;
                  try { const l = require(`../../images/brand_logos/${b.logo}`); logoSrc = typeof l === 'object' ? l.default : l; } catch (e) {}
                  return (
                    <Link key={b.slug} to={`/brands/${b.slug}`} className="rr-brand-card">
                      {logoSrc ? <img src={logoSrc} alt={b.label} className="rr-brand-card__logo" loading="lazy" /> : <div className="rr-brand-card__logo" style={{ fontSize: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{b.label.charAt(0)}</div>}
                      <span className="rr-brand-card__label">{b.label}</span>
                      <span className="rr-brand-card__count">{b.models.length} models</span>
                    </Link>
                  );
                })}
              </div>
              {!isSpecific && (
                <div className="rr-text-center rr-mt-32">
                  <Link to="/brands" className="rr-cta-dark-outline">View All Brands</Link>
                </div>
              )}
            </SectionWrapper>
          );
        })()}

        {relatedBlogs.length > 0 && (
          <SectionWrapper variant="gray" kicker="From Our Blog" heading="Expert Tips & Guides">
            <div className="rr-card-grid--3col">
              {relatedBlogs.map((blog, i) => (
                <BlogCard
                  key={i}
                  slug={blog.slug}
                  title={blog.title}
                  excerpt={blog.excerpt || blog.description}
                  image={blog.image || blog.thumbnail}
                  category={blog.category}
                  date={blog.date}
                  readTime={blog.readTime}
                />
              ))}
            </div>
          </SectionWrapper>
        )}

        <RelatedTopServices variant="keyword" kicker="Popular Cities" heading="Doorstep Service in Top Cities" />

        <FinalCTA
          heading={<>Need {h1}?<br />Book Now &mdash; Get It Done Today.</>}
          subtitle={`Doorstep service \u00B7 Certified mechanics \u00B7 ${pricingHint} onwards \u00B7 30-day warranty`}
          ctas={[
            { label: 'Book Now', href: '/service', variant: 'primary' },
            { label: 'Call +91 120 361 5050', href: 'tel:+911203615050', variant: 'white-outline' },
          ]}
        />

        <Footer
          isMobileBrowser={isMobileBrowser}
          isMobileApp={isMobileApp}
          setCitiesModal={setCitiesModal}
        />
      </div>
    );
  }
}

export default withRouter(KeywordLanding);
